import {
    CreateImageRequest,
    CreateOrganizationRequest,
    CreateOrganizationResponse,
    KlavioEventPropertyKey,
    KlayvioEvents,
    OrganizationPermissionLevel,
    OrganizationRoleType,
    RoleAssignment,
    TopicPermissionLevel,
} from '../types';
import { LitElement, css, html } from 'lit';
import { addRoleForEntity, createRoleRequestFromRole, removeRoleForEntity } from '../helpers/api/roles-api';
import { arrayBufferToBase64, isDefined, undefinedIfEmpty } from '../helpers/utils';
import { customElement, property, query, state } from 'lit/decorators';
import { getRandomString, validateEmail } from '../helpers/strings';

import accountInfo from '../helpers/state/account-info';
import { createOrganization } from '../helpers/api/organization-api';
import featuresInfo from '../helpers/state/features-info';
import { getMainPage } from '../helpers/page-name';
import { logEventWithKlayvio } from '../helpers/api/api';
import organizationInfo from '../helpers/state/organization-info';
import topicData from '../helpers/state/topic-data';
import { uploadImage } from '../helpers/api/image-api';
import { validateIcon } from '../helpers/tags-categories';

@customElement('manage-team-access-component')
export class ManageTeamAccessComponent extends LitElement {
    static amplitude_callsite: string = 'Manage Teammates Access Modal';


    @query('#colorPickerPrimary')
    private readonly colorPickerPrimaryElement!: HTMLInputElement;

    @query('#colorPickerSecondary')
    private readonly colorPickerSecondaryElement!: HTMLInputElement;

    @property({ type: Boolean })
    primaryColorOpened?: boolean;

    @property({ type: Boolean })
    secondaryColorOpened?: boolean;



    @state()
    orgTitle: string | undefined;

    @state()
    iconUrl: string | undefined;

    @state()
    primaryColor: string | undefined = '#000000';

    @state()
    secondaryColor: string | undefined = '#FFFFFF';

    @property({ type: Boolean })
    isCreating?: boolean;

    @state()
    rolesForOrganization?: RoleAssignment[];

    @state()
    rolesForTopic?: RoleAssignment[];

    teamMemberRoles = [
        OrganizationPermissionLevel.ADMINISTRATOR,
        OrganizationPermissionLevel.OWNER,
        OrganizationPermissionLevel.COLLABORATOR,
    ];

    guestRoles = [
        OrganizationPermissionLevel.LIMITED,
    ];

    protected async firstUpdated() {
       await this.loadRoles();
    }

    async loadRoles() {
        this.rolesForOrganization = await organizationInfo.getOrganizationRoles();
        this.rolesForTopic = await topicData.getTopicRoles();
    }

    private async handleSubmit(e: any) {
        const email = e.detail.value.toLowerCase().trim();
        await getMainPage().errorToastElement.tryOrErrorToast(async () => {
            const topicRoles = this.rolesForTopic?.map((r) => r.email) ?? [];
            if (email === '' || topicRoles.includes(email)) {
                getMainPage().errorToastElement.showToast(
                    'Email cannot be an empty string or a duplicate of an existing email'
                );
                return;
            }
            if (!validateEmail(email)) {
                getMainPage().errorToastElement.showToast('Invalid email format.');
                return;
            }
            if (!undefinedIfEmpty(e.detail.option)) {
                getMainPage().errorToastElement.showToast('Access type not set.');
                return;
            }

            if (organizationInfo.id === undefined) {
                getMainPage().errorToastElement.showToast('Organization ID not found.');
                return;
            }


            await addRoleForEntity(
                ManageTeamAccessComponent.amplitude_callsite,
                organizationInfo.organization?._id.$oid ?? '',
                {
                    email,
                    level: {
                        OrganizationPermissionLevelType: OrganizationPermissionLevel.LIMITED,
                    },
                }
            );

            await addRoleForEntity(
                ManageTeamAccessComponent.amplitude_callsite,
                topicData.topicId,
                {
                    email,
                    level: {
                        TopicPermissionLevelType: TopicPermissionLevel.WRITER
                    },
                }
            );
            await this.loadRoles();

            getMainPage().errorToastElement.showToast('New access added');
        }, 'An error has occurred.  Context: could not update the organization.');
        this.requestUpdate();
    }

    private async removeMemberFromTopic(e: CustomEvent) {
        if (this.rolesForTopic !== undefined && this.rolesForTopic.length === 1) {
            getMainPage().errorToastElement.showToastAndThrow('Maps must have at least one admin.');
        }
        await getMainPage().errorToastElement.tryOrErrorToast(async () => {
            if (this.rolesForTopic === undefined || topicData.topicId === undefined)
                return;
            const role_to_delete = this.rolesForTopic.filter((w) => w.email === e.detail.email);
            await removeRoleForEntity(
                ManageTeamAccessComponent.amplitude_callsite,
                topicData.topicId,
                createRoleRequestFromRole(role_to_delete[0])
            );
            try {
                logEventWithKlayvio(KlayvioEvents.RemovedEditor, accountInfo.email, [
                    { key: KlavioEventPropertyKey.MapName, value: topicData.topic?.name ?? '' },
                    { key: KlavioEventPropertyKey.MapId, value: topicData.topic?._id.$oid ?? '' },
                    { key: KlavioEventPropertyKey.RemovedEmail, value: role_to_delete[0].email }
                ]);
            } catch (e) {
                console.error(e);
            }

            this.dispatchEvent(new CustomEvent('updated', {}));
        }, 'An error has occurred.  Context: could not update the account.');
        await this.requestUpdate();
    }

    private openColorPicker(type: string) {
        if (type === 'primary_color') {
            this.colorPickerPrimaryElement.focus();
            this.colorPickerPrimaryElement.click();
            this.primaryColorOpened = true;
            this.secondaryColorOpened = false;
        } else if (type === 'secondary_color') {
            this.colorPickerSecondaryElement.focus();
            this.colorPickerSecondaryElement.click();
            this.primaryColorOpened = false;
            this.secondaryColorOpened = true;
        }
    }

    private async submitColor(type: string) {
        if (type === 'primary_color') {
            this.primaryColor = this.colorPickerPrimaryElement.value;
        } else if (type === 'secondary_color') {
            this.secondaryColor = this.colorPickerSecondaryElement.value;
        }
        this.primaryColorOpened = false;
        this.secondaryColorOpened = false;
    }

    async onChangeIconUrl(e: CustomEvent) {
        const validatedIcon = await validateIcon(e.detail?.imageUrl);
        if (!validatedIcon) {
            getMainPage().errorToastElement.showToastAndThrow('The icon URL entered is invalid');
        }
        this.iconUrl = e.detail?.imageUrl;
    }

    async onChangeImage(e: CustomEvent) {
        const imageToUpload = e.detail?.imageToUpload;
        const imageRequest: CreateImageRequest = {
            name: imageToUpload.file.name,
            data: arrayBufferToBase64(await imageToUpload.file.arrayBuffer()),
        };
        this.iconUrl = await uploadImage(ManageTeamAccessComponent.amplitude_callsite, imageRequest, imageToUpload.orientation);
    }

    onClose() {
        this.dispatchEvent(new CustomEvent('closed', {}));
    }

    async onCreateTeam() {
        const organization: CreateOrganizationRequest = {
            name: this.orgTitle ?? '',
            logo: this.iconUrl ?? '',
            primary_color: this.primaryColor ?? '#000000',
            secondary_color: this.secondaryColor ?? '#FFFFFF',
            sso: `org-${getRandomString(4)}`,
            default_map_template_id: undefined,
            enterprise_billing: false,
            custom_font: undefined,
            cname: undefined,
            demographics: undefined,
        };
        let new_org: CreateOrganizationResponse | undefined;
        await getMainPage().errorToastElement.tryOrErrorToast(async () => {
            new_org = await createOrganization(ManageTeamAccessComponent.amplitude_callsite, organization, {});
            getMainPage().errorToastElement.showToast('New workspace created');
        }, 'An error has occurred.  Context: could not create an organization.');
        this.dispatchEvent(new CustomEvent('team-created', {
            detail: { organization: new_org }
        }));
    }

    renderCreatingTeam() {
        return html`
            <div class="grantAccessContent">
                <h2 id="grantAccessTitle">
                    Create a new workspace
                </h2>
                <div>
                    <!-- Name -->
                    <div class="innerItem">
                        <div class="editWrapper">
                            <div class="labelColumn">
                                <label class="formLabel">Workspace name</label>
                            </div>
                            <div id="editNameSection" class="editSection">
                                <form-input
                                    id="name"
                                    type="text"
                                    .value="${this.orgTitle ?? ''}"
                                    @change="${async (e: any) => { this.orgTitle = e.detail.value; }}"
                                    maxLength="60"
                                >
                                </form-input>
                            </div>
                        </div>
                    </div>
                    <!-- Logo -->
                    <div class="innerItem">
                        <div class="editWrapper">
                            <div class="labelColumn">
                                <label class="formLabel">Workspace logo</label>
                            </div>
                            <div id="editLogoSection" class="editSection">
                                <image-upload-component
                                    .imageUrl="${this.iconUrl ?? ''}"
                                    @on-change-image="${this.onChangeImage}"
                                    @changed-image-url=${this.onChangeIconUrl}
                                ></image-upload-component>
                            </div>
                        </div>
                    </div>
                    <div class="innerItem flexContainer alignCenter">
                        <!-- Primary Color -->
                        <div class="colorElementWrapper">
                            <div class="editWrapper">
                                <label class="formLabel">Primary Brand Color</label>
                            </div>
                            <div class="colorElement flexContainer alignCenter">
                                ${this.primaryColorOpened === true ? html`
                                    <mwc-icon-button
                                        class="editIcon done submitButton"
                                        icon="done"
                                        @click="${async () => await this.submitColor('primary_color')}"
                                    ></mwc-icon-button>
                                ` : ''}
                                <div class="flexContainer alignCenter pickerWrapper">
                                    <span id="colorPrimary" class="colorSpan" style="background: ${this.primaryColor}"></span>
                                    <input
                                        id="colorPickerPrimary"
                                        class="colorPicker"
                                        type="color"
                                    />
                                    <mwc-icon-button
                                        class="pickerImage"
                                        icon="colorize"
                                        @click="${() => this.openColorPicker('primary_color')}"
                                    ></mwc-icon-button>
                                </div>
                            </div>
                        </div>
                        <!-- Secondary Color -->
                        <div class="colorElementWrapper">
                            <div class="editWrapper">
                                <label class="formLabel">Secondary Brand Color</label>
                            </div>
                            <div class="colorElement flexContainer alignCenter">
                                ${this.secondaryColorOpened === true ? html`
                                    <mwc-icon-button
                                        class="editIcon done submitButton"
                                        icon="done"
                                        @click="${async () => await this.submitColor('secondary_color')}"
                                    ></mwc-icon-button>
                                ` : ''}
                                <div class="flexContainer alignCenter pickerWrapper">
                                    <span id="colorSecondary" class="colorSpan" style="background: ${this.secondaryColor}"></span>
                                    <input
                                        id="colorPickerSecondary"
                                        class="colorPicker"
                                        type="color"
                                    />
                                    <mwc-icon-button
                                        class="pickerImage"
                                        icon="colorize"
                                        @click="${() => this.openColorPicker('secondary_color')}"
                                    ></mwc-icon-button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="buttonsWrapper flexContainer flexEnd">
                    <form-button size="lg" variant="outline" class="button" @click=${this.onClose}>
                        Close
                    </form-button>
                    <form-button size="lg" class="button" backgroundColor="var(--main-success-color)" @click="${this.onCreateTeam}">
                        Create Team
                    </form-button>
                </div>
                <error-toast id="errorToast"></error-toast>
                <div></div>
            </div>
        `;
    }

    private goToTeamSettingsPage() {
        if (organizationInfo.organization?.is_administrator === true) {
            window.location.assign('/#home?page_name=Team%20Settings&setting_type=ManageTeamMembers');
        } else {
            getMainPage().errorToastElement.showToast('Only workspace admins can update team members');
        }
    }

    render() {
        if (this.isCreating === true) {
            return this.renderCreatingTeam();
        }

        const teamMembers = this.rolesForOrganization?.filter((r) => this.teamMemberRoles.includes((r.role as OrganizationRoleType).OrganizationRoleType?.level));

        const totalTeamMembersCount = teamMembers?.length;
        const haveEmail = this.rolesForOrganization?.find(r => r.email === accountInfo.email);

        return html`
            <div class="grantAccessContent">
                <h2 id="grantAccessTitle">
                    Manage Access
                </h2>
                <div class="flexContainer alignCenter justifyBetween" style="margin-bottom: 0.5rem">
                    ${isDefined(topicData?.topic?.organization_id) ? html`
                        <h2 class="partTitle">
                            Workspace Access
                        </h2>
                    ` : ''}
                </div>
                <p class="role-info"> Workspace members have full access to paid features and settings.</p>
                ${!isDefined(organizationInfo?.id) ? html`
                    <div class="createTeamButton" @click=${async () => this.dispatchEvent(new CustomEvent('updated', { detail: { isCreating: true } }))}>
                        <mwc-icon>add</mwc-icon>
                        <span>Create a workspace with Proxi Pro</span>
                    </div>
                ` : html`
                    ${isDefined(totalTeamMembersCount) ? html`
                        <div class="innerBox">
                            <div class="headingContainer flexContainer alignCenter justifyBetween" style="gap: 1rem">
                                <div class="flexContainer alignCenter" style="gap: 1rem">
                                    <img src="${organizationInfo.organization?.logo ?? ''}" style="height: 42px" />
                                    <div class="headerTitleSection">
                                        <div class="nameHeading">${organizationInfo.organization?.name ?? ''}</div>
                                        <div class="membersHeading">${totalTeamMembersCount} member${(totalTeamMembersCount && totalTeamMembersCount > 1) ? 's' : ''}</div>
                                    </div>
                                </div>
                                ${organizationInfo.organization?.is_administrator === true ? html`
                                    <form-button size="xs" variant="outline" class="button" @click=${this.goToTeamSettingsPage}>
                                        <mwc-icon>settings</mwc-icon>
                                    </form-button>
                                ` : ''}
                            </div>
                            ${this.rolesForOrganization
                                ?.filter(
                                    (r) =>
                                        (r.role as OrganizationRoleType).OrganizationRoleType?.level ===
                                        OrganizationPermissionLevel.ADMINISTRATOR
                                )
                                .map(
                                    (c) => html`
                                        <access-item
                                            email="${c.email}"
                                            accessLevel="Administrator"
                                        >
                                        </access-item>
                                    `
                                )}
                            ${this.rolesForOrganization
                                ?.filter(
                                    (r) =>
                                        (r.role as OrganizationRoleType).OrganizationRoleType?.level ===
                                        OrganizationPermissionLevel.OWNER
                                )
                                .map(
                                    (c) => html`
                                        <access-item
                                            email="${c.email}"
                                            accessLevel="Editor"
                                        >
                                        </access-item>
                                    `
                                )}
                            ${this.rolesForOrganization
                                ?.filter(
                                    (r) =>
                                        (r.role as OrganizationRoleType).OrganizationRoleType?.level ===
                                        OrganizationPermissionLevel.COLLABORATOR
                                )
                                .map(
                                    (c) => html`
                                        <access-item
                                            email="${c.email}"
                                            accessLevel="Collaborator"
                                        >
                                        </access-item>
                                    `
                                )}
                        </div>
                    ` : ''}
                `
                }

                <div style="margin-top: 1.5rem; margin-bottom: 0.5rem">
                    <h2 class="partTitle">
                        Guest Editors on This Map
                    </h2>
                    <p class="role-info">
                        Guests can add, edit, and delete points only on maps they are explicitly invited to. They cannot access or use paid or advanced features. You must start a subscription to invite guests.
                    </p>
                    ${isDefined(haveEmail) ? html`
                        <div class="inviteGuestSection">
                            ${featuresInfo.addMapEditorsEnabled ? html`
                                <p class="membersHeading">Invite a Guest</p>
                                <form-input-access
                                    type="topic"
                                    placeholder="Email Address"
                                    @submit="${this.handleSubmit}"
                                    ?isOwner=${organizationInfo.organization?.is_collaborator}
                                    ?hideRole=${true}
                                >
                                    Send Invite
                                </form-input-access>
                            ` : ''}
                        </div>
                    ` : ''}
                </div>
                ${isDefined(this.rolesForTopic) && this.rolesForTopic?.length > 0 ? html`
                    <div class="innerBox">
                        ${this.rolesForTopic
                            ?.filter((role) => organizationInfo.getUserOrganizationRoleLevel(role.email)?.role.OrganizationRoleType?.level === OrganizationPermissionLevel.LIMITED)
                            .map((c) => html`
                                <access-item
                                    email="${c.email}"
                                    ?canDelete="${organizationInfo.organization?.is_administrator}"
                                    @removed="${this.removeMemberFromTopic}"
                                    accessLevel="Guest"
                                >
                                </access-item>
                            `)
                        }
                    </div>
                ` : ''}
                <error-toast id="errorToast"></error-toast>
            </div>
        `;
    }

    static get styles() {
        return css`
            .grantAccessContent {
                width: 550px;
                padding: 0.5rem 1.5rem 1rem;
            }

            #grantAccessTitle {
                color: var(--main-primary-color);
                display: flex;
                justify-content: center;
                font-size: 1.25rem;
                margin: 0.25rem;
                align-items: center;
            }

            .role-info {
                font-size: 0.75rem;
                line-height: 0.8rem;
                margin-top: 0;
            }

            .partTitle {
                font-size: 16px;
                font-weight: 600;
                margin: 0;
            }

            .headingContainer {
                padding: 0.5rem 1.5rem;
                background: var(--theme-opacity-background-color);
                border-bottom: 1px solid var(--theme-border-color);
            }

            .nameHeading {
                font-size: 1rem;
                font-weight: 700;
                color: var(--main-primary-color);
            }

            .inviteGuestSection {
                display: flex;
                flex-direction: row;
                align-items: center;
                justify-content: center;
                gap: 1rem;
            }

            .membersHeading {
                font-size: 14px;
            }

            .flexContainer {
                display: flex;
            }

            .flexEnd {
                justify-content: flex-end;
            }

            .alignCenter {
                align-items: center;
            }

            .justifyBetween {
                justify-content: space-between;
            }

            .innerBox {
                border-radius: 0.75rem;
                border: 1px solid var(--theme-border-color);
                overflow: hidden;
                box-shadow: rgba(16, 24, 40, 0.1) 0px 1px 3px;
            }

            .colorPicker {
                opacity: 0;
                width: 0px;
                margin: 0px;
            }

            .colorSpan {
                border-radius: 50%;
                width: 2.75rem;
                height: 2.75rem;
                display: inline-block;
                box-shadow: rgba(0, 0, 0, 0.3) 0px 1px 4px -1px;
            }

            .buttonsWrapper {
                width: 100%;
                gap: 0.75rem;
                position: sticky;
                bottom: -1.25rem;
                padding-top: 1rem;
                background: white;
                padding-bottom: 1rem;
            }

            .formLabel {
                color: var(--theme-input-color);
                font-size: 1rem;
                font-weight: bold;
                display: block;
            }

            .formCaption {
                margin-top: 0;
                font-weight: 500;
                color: var(--theme-light-text-color);
            }

            .innerItem {
                padding-top: 1rem;
                padding-bottom: 1rem;
                flex: 1;
            }

            .colorElementWrapper {
                flex: 1;
            }

            .colorElement {
                margin-top: 0.75rem;
            }

            .createTeamButton {
                display: flex;
                align-items: center;
                justify-content: center;
                cursor: pointer;
                background-color: var(--theme-light-scrollbar-color);
                font-weight: 500;
                gap: 0.75rem;
                padding: 1.5rem;
                margin-top: 1rem;
                margin-bottom: 1rem;
                border-radius: 0.75rem;
                background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='12' ry='12' stroke='%23D0D5DDFF' stroke-width='2' stroke-dasharray='5%2c8' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e");
            }

            access-item {
                display: block;
                border-bottom: 1px solid var(--theme-border-color);
                padding-top: 0.25rem;
                padding-bottom: 0.25rem;
                padding-left: 1.5rem;
                padding-right: 1.5rem;
            }

            access-item:last-of-type {
                border-bottom: none;
            }

            @media (max-width: 900px) {
                .grantAccessContent {
                    width: calc(100% - 2rem);
                    padding: 0.5rem 1rem 0;
                }
            }
        `;
    }
}
